<template>
  <div class="approval-detail">
    <el-card shadow="never" v-loading="loading">
      <template #header>
        <div class="card-header">
          <el-button type="text" @click="handleBack">
            <el-icon><ArrowLeft /></el-icon>
            返回
          </el-button>
          <span class="title">审批详情</span>
          <div></div>
        </div>
      </template>

      <div v-if="approvalDetail && approvalDetail.processName !== undefined" class="detail-content">
        <!-- 审批信息 -->
        <el-card shadow="never" class="info-card">
          <template #header>
            <span class="section-title">审批信息</span>
          </template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="流程名称">
              {{ approvalDetail.processName || '未命名' }}
            </el-descriptions-item>
            <el-descriptions-item label="业务类型">
              <el-tag :type="getBusinessTypeTagType(approvalDetail.businessType)">
                {{ getBusinessTypeName(approvalDetail.businessType) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="审批状态">
              <el-tag :type="getStatusTagType(approvalDetail.approvalStatus)">
                {{ getStatusName(approvalDetail.approvalStatus || '') }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="业务员" v-if="approvalDetail.salesmanName">
              {{ approvalDetail.salesmanName }}
            </el-descriptions-item>
            <el-descriptions-item label="提交时间">
              {{ formatDateTime(approvalDetail.createTime) }}
            </el-descriptions-item>
            <el-descriptions-item label="审批时间" v-if="approvalDetail.approvalTime">
              {{ formatDateTime(approvalDetail.approvalTime) }}
            </el-descriptions-item>
            <el-descriptions-item label="审批人">
              {{ approvalDetail.approver || '-' }}
            </el-descriptions-item>
            <el-descriptions-item
              label="驳回原因"
              v-if="approvalDetail.approvalStatus === 'REJECTED' && approvalDetail.rejectReason"
              :span="2"
            >
              <span class="reject-reason">{{ approvalDetail.rejectReason }}</span>
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- 业务详情 -->
        <el-card shadow="never" class="info-card" v-if="approvalDetail.businessType && approvalDetail.businessId">
          <template #header>
            <span class="section-title">业务详情</span>
          </template>
          <BusinessDetail
            :business-type="approvalDetail.businessType"
            :business-id="approvalDetail.businessId"
          />
        </el-card>

        <!-- 审批记录时间轴 -->
        <el-card shadow="never" class="info-card" v-if="approvalRecords.length > 0">
          <template #header>
            <span class="section-title">审批记录</span>
          </template>
          <ApprovalTimeline v-if="approvalRecords && approvalRecords.length > 0" :records="approvalRecords" />
        </el-card>

        <!-- 操作按钮 -->
        <div class="action-bar" v-if="showActionButtons">
          <el-button type="danger" @click="handleReject" :loading="processing" class="action-btn">
            驳回
          </el-button>
          <el-button type="success" @click="handleApprove" :loading="processing" class="action-btn">
            通过
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- 驳回对话框 -->
    <el-dialog
      v-model="rejectDialogVisible"
      title="驳回审批"
      width="500px"
      @close="rejectReason = ''"
    >
      <el-form>
        <el-form-item label="驳回意见">
          <el-input
            v-model="rejectReason"
            type="textarea"
            :rows="4"
            placeholder="请输入驳回意见"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="rejectDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="handleSubmitReject" :loading="processing">
          确认驳回
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowLeft } from '@element-plus/icons-vue'
import ApprovalTimeline from '@/components/ApprovalTimeline.vue'
import BusinessDetail from '@/components/BusinessDetail.vue'
import {
  openApprovalDetail,
  getApprovalRecords,
  updateApprovalStatus
} from '@/api/managerApproval'
import { hasRoutePermission } from '@/utils/permission'

const route = useRoute()
const router = useRouter()
// 从 Home.vue 注入的关闭标签方法（兜底，防止事件监听异常）
const closeTab = inject('closeTab', null)

// 数据
const loading = ref(false)
const processing = ref(false)
const approvalDetail = ref(null)
const approvalRecords = ref([])
const rejectDialogVisible = ref(false)
const rejectReason = ref('')
// 记录最后一次审批操作的状态，用于返回时自动切换列表状态
const lastApprovalAction = ref(null)
// 记录打开详情时的初始状态，用于判断是否需要自动切换到审批中状态
const initialStatus = ref(null)
// 记录是否进行了审批操作（通过或驳回）
const hasPerformedAction = ref(false)
// 记录审批操作前的状态，用于减少角标数量
const previousApprovalStatus = ref(null)

// 获取用户有权限的审批列表路径
const getApprovalListPath = () => {
  // 1. 优先根据当前登录用户的角色类型来判断应该回到哪个列表
  try {
    const userInfoStr = localStorage.getItem('userInfo')
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr || '{}')
      const userType = (userInfo.userType || '').toUpperCase()

      // 管理员 / 系统管理员：走管理员审批列表
      if (userType === 'MANAGER' || userType === 'SYSTEM_ADMIN') {
        return '/home/manager-approval'
      }

      // 内勤相关角色：统一走内勤审批列表
      // 这里兼容多种可能的内勤类型命名
      if (
        userType === 'INTERNAL' ||
        userType === 'ASSISTANT' ||
        userType === 'ACCOUNTANT' ||
        userType === 'INTERNAL_STAFF'
      ) {
        return '/home/internal-approval'
      }
    }
  } catch (e) {
    console.warn('读取用户信息失败:', e)
  }

  // 2. 其次，根据上一次记录的审批列表路径进行返回（比如从哪个列表点进来的）
  try {
    const lastApprovalListPath = sessionStorage.getItem('lastApprovalListPath')
    if (lastApprovalListPath && hasRoutePermission(lastApprovalListPath)) {
      return lastApprovalListPath
    }
  } catch (e) {
    console.warn('读取上次审批列表路径失败:', e)
  }
  
  // 3. 最后兜底：根据菜单权限判断（老逻辑）
  // 优先检查内部审批管理（内勤使用）
  if (hasRoutePermission('/home/internal-approval')) {
    return '/home/internal-approval'
  }
  // 其次检查管理员审批管理
  if (hasRoutePermission('/home/manager-approval')) {
    return '/home/manager-approval'
  }
  // 默认返回管理员审批管理
  return '/home/manager-approval'
}

// 计算属性：显示操作按钮
const showActionButtons = computed(() => {
  try {
    if (!approvalDetail.value || !approvalDetail.value.approvalStatus) {
      return false
    }
    
    // 获取当前用户信息
    let userInfo = {}
    try {
      const userInfoStr = localStorage.getItem('userInfo')
      if (userInfoStr) {
        userInfo = JSON.parse(userInfoStr)
      }
    } catch (e) {
      console.warn('解析用户信息失败:', e)
      userInfo = {}
    }
    const currentUserId = userInfo.userId
    const currentUser = userInfo.username || userInfo.realName || ''
    
    // 如果审批记录已加载，检查当前用户所在节点的状态
    if (approvalRecords.value && approvalRecords.value.length > 0) {
      // 查找当前用户所在的节点记录
      const userRecord = approvalRecords.value.find(r => {
        // 通过用户ID匹配
        if (currentUserId && r.approverId && String(r.approverId) === String(currentUserId)) {
          return true
        }
        // 通过用户名匹配
        if (currentUser && (
          (r.approver && r.approver.includes(currentUser)) ||
          (r.approverName && r.approverName.includes(currentUser))
        )) {
          return true
        }
        return false
      })
      
      if (userRecord) {
        // 检查节点状态
        const nodeStatus = (userRecord.approvalStatus || '').toUpperCase().trim()
        // 等待中状态不显示按钮
        if (nodeStatus === 'WAITING') {
          return false
        }
        // 只有待审批或审批中状态才显示按钮
        return nodeStatus === 'PENDING' || nodeStatus === 'APPROVING'
      }
    }
    
    // 如果没有找到用户记录，使用整体审批状态作为备用判断
    const status = (approvalDetail.value.approvalStatus || '').toUpperCase()
    // 等待中状态不显示按钮
    if (status === 'WAITING') {
      return false
    }
    // 待审批或审批中状态时显示按钮
    return status === 'PENDING' || status === 'APPROVING'
  } catch (error) {
    console.error('计算 showActionButtons 失败:', error)
    return false
  }
})

// 加载审批详情
const loadApprovalDetail = async () => {
  const approvalId = route.params.id
  if (!approvalId) {
    ElMessage.error('参数错误')
    router.back()
    return
  }

  loading.value = true
  try {
    // 打开审批详情（后端会将 PENDING 状态改为 APPROVING）
    const openRes = await openApprovalDetail(approvalId)
    if (openRes.code !== '000000' || !openRes.data) {
      throw new Error(openRes.msg || '获取审批详情失败')
    }

    // 后端可能返回 { approval: {...}, nodes: [...] } 或直接返回 approval 对象
    const responseData = openRes.data
    let approvalInfo = null
    let flowNodes = [] // 审批流程节点配置
    
    if (responseData && typeof responseData === 'object') {
      // 如果返回的是包含 approval 和 nodes 的对象
      if (responseData.approval) {
        approvalInfo = responseData.approval
        // 保存节点配置信息
        if (responseData.nodes && Array.isArray(responseData.nodes)) {
          flowNodes = responseData.nodes
        }
      } else {
        // 直接返回 approval 对象
        approvalInfo = responseData
      }
    } else {
      approvalInfo = responseData
    }
    
    if (!approvalInfo) {
      throw new Error('审批详情数据为空')
    }
    
    // 记录打开详情后的状态
    // 如果当前状态是 APPROVING，且没有进行审批操作，返回时自动切换到审批中状态
    const currentStatus = (approvalInfo.approvalStatus || '').toUpperCase()
    initialStatus.value = currentStatus
    
    // 记录审批操作前的状态，用于在返回列表时重新加载正确的角标数量
    // 优先使用从列表页传入的 previousStatus（表示用户是从“待审批”还是“审批中”等状态点进来的）
    // 如果路由上没有传入，则再回退到当前的审批状态
    const previousStatusFromRoute = (route.query?.previousStatus || '').toString().toUpperCase()
    if (previousStatusFromRoute) {
      previousApprovalStatus.value = previousStatusFromRoute
    } else if (currentStatus === 'PENDING' || currentStatus === 'APPROVING') {
      // 兼容老数据：如果没有传入 previousStatus，且当前状态是待审批或审批中，则使用当前状态
      previousApprovalStatus.value = currentStatus
    }
    console.log('记录审批操作前的状态(previousStatus):', previousApprovalStatus.value)
    
    // 调试信息：打印审批详情数据
    console.log('审批详情原始数据:', approvalInfo)
    console.log('审批状态:', approvalInfo.approvalStatus)
    console.log('记录的初始状态:', initialStatus.value)
    
    approvalDetail.value = approvalInfo
    
    // 确保 approvalStatus 正确赋值
    if (approvalInfo.approvalStatus) {
      approvalDetail.value.approvalStatus = approvalInfo.approvalStatus
      console.log('设置后的审批状态:', approvalDetail.value.approvalStatus)
    }

    // 获取审批记录并合并节点配置
    if (approvalInfo.businessType && approvalInfo.businessId) {
      const recordsRes = await getApprovalRecords({
        businessType: approvalInfo.businessType,
        businessId: approvalInfo.businessId
      })
      
      // 获取已审批的记录
      const approvedRecords = recordsRes.code === '000000' ? (recordsRes.data || []) : []
      
      // 获取整个审批的状态
      const overallStatus = approvalInfo.approvalStatus ? String(approvalInfo.approvalStatus).toUpperCase().trim() : null
      const isOverallApproved = overallStatus === 'APPROVED'
      const isOverallRejected = overallStatus === 'REJECTED'
      
      // 调试信息：打印审批记录
      console.log('审批记录数据:', approvedRecords)
      console.log('节点配置:', flowNodes)
      console.log('整个审批状态:', overallStatus, '是否已通过:', isOverallApproved)
      
      // 如果有节点配置，合并节点配置和审批记录，显示所有节点
      if (flowNodes && flowNodes.length > 0) {
        // 首先检查是否有节点被拒绝，以及被拒绝的节点位置
        const rejectedRecordIndex = approvedRecords.findIndex(r => {
          const status = (r.approvalStatus || '').toUpperCase().trim()
          return status === 'REJECTED'
        })
        const hasRejected = rejectedRecordIndex !== -1
        
        // 找到被拒绝节点在流程中的位置（通过节点顺序或索引）
        let rejectedNodeIndex = -1
        if (hasRejected) {
          const rejectedRecord = approvedRecords[rejectedRecordIndex]
          rejectedNodeIndex = flowNodes.findIndex((node, nodeIndex) => {
            // 通过节点ID匹配（最准确）
            if (rejectedRecord.nodeId && node.id && String(rejectedRecord.nodeId) === String(node.id)) {
              return true
            }
            // 通过 currentNode 字段匹配
            if (rejectedRecord.currentNode && node.nodeName && String(rejectedRecord.currentNode).trim() === String(node.nodeName).trim()) {
              return true
            }
            // 通过节点顺序匹配
            const nodeOrder = node.nodeOrder !== undefined ? node.nodeOrder : (nodeIndex + 1)
            if (rejectedRecord.nodeOrder !== undefined && rejectedRecord.nodeOrder === nodeOrder) {
              return true
            }
            return false
          })
          
          // 如果通过上述方式找不到，尝试通过节点顺序找到第一个被驳回的节点
          if (rejectedNodeIndex === -1 && rejectedRecord.nodeOrder !== undefined) {
            rejectedNodeIndex = flowNodes.findIndex((node, nodeIndex) => {
              const nodeOrder = node.nodeOrder !== undefined ? node.nodeOrder : (nodeIndex + 1)
              return nodeOrder === rejectedRecord.nodeOrder
            })
          }
          
          // 如果还是找不到，使用节点顺序作为备用方案
          if (rejectedNodeIndex === -1 && rejectedRecord.nodeOrder !== undefined) {
            // 直接使用 nodeOrder - 1 作为索引（因为 nodeOrder 通常从 1 开始）
            rejectedNodeIndex = rejectedRecord.nodeOrder - 1
            if (rejectedNodeIndex < 0 || rejectedNodeIndex >= flowNodes.length) {
              rejectedNodeIndex = -1
            }
          }
          
          // 调试信息
          console.log('被拒绝节点信息:', {
            rejectedRecord,
            rejectedRecordIndex,
            rejectedNodeIndex,
            hasRejected,
            rejectedRecordNodeOrder: rejectedRecord.nodeOrder,
            rejectedRecordNodeId: rejectedRecord.nodeId,
            rejectedRecordCurrentNode: rejectedRecord.currentNode,
            flowNodesCount: flowNodes.length
          })
        }
        
        // 获取整个审批记录的 currentNodeIndex（用于判断节点是否已审批）
        const overallRecord = approvedRecords.length > 0 ? approvedRecords[0] : null
        const overallCurrentNodeIndex = overallRecord?.currentNodeIndex !== undefined ? overallRecord.currentNodeIndex : null
        
        const mergedRecords = flowNodes.map((node, index) => {
          // 节点的顺序（从1开始）
          const nodeOrder = node.nodeOrder !== undefined ? node.nodeOrder : (index + 1)
          
          // 查找该节点对应的审批记录
          // 注意：workflow_approval 表存储的是整个审批流程的记录，不是每个节点的单独记录
          // 所以需要通过 currentNodeIndex、approver、currentNode 等字段来判断节点状态
          let record = approvedRecords.find(r => {
            // 通过节点ID匹配（最准确）
            if (r.nodeId && node.id && String(r.nodeId) === String(node.id)) {
              return true
            }
            // 通过 currentNode 字段匹配
            if (r.currentNode && node.nodeName && String(r.currentNode).trim() === String(node.nodeName).trim()) {
              return true
            }
            // 通过节点顺序匹配
            if (r.nodeOrder !== undefined && node.nodeOrder !== undefined && r.nodeOrder === nodeOrder) {
              return true
            }
            // 通过 currentNodeIndex 匹配：如果 currentNodeIndex >= nodeOrder，说明该节点已审批
            // 且审批人是当前节点的审批人，则认为是该节点的记录
            if (r.currentNodeIndex !== undefined && r.currentNodeIndex >= nodeOrder) {
              if (r.approver && node.approverName && 
                  (r.approver.includes(node.approverName) || node.approverName.includes(r.approver))) {
                return true
              }
            }
            return false
          })
          
          // 注意：节点状态应该是独立的，只有找到实际的审批记录时才使用记录的状态
          // 不要通过 currentNodeIndex 或审批人匹配来推断节点状态，这会导致后续节点错误显示为"已通过"
          
          // 调试信息：打印节点匹配情况
          if (index === 0 || !record) {
            console.log(`节点 ${node.nodeName || index + 1} 匹配情况:`, {
              nodeIndex: index,
              nodeId: node.id,
              nodeName: node.nodeName,
              nodeOrder: node.nodeOrder !== undefined ? node.nodeOrder : (index + 1),
              approverName: node.approverName,
              foundRecord: !!record,
              overallCurrentNodeIndex: overallCurrentNodeIndex,
              isOverallApproved: isOverallApproved,
              record: record ? {
                id: record.id,
                nodeId: record.nodeId,
                nodeName: record.nodeName,
                currentNode: record.currentNode,
                currentNodeIndex: record.currentNodeIndex,
                nodeOrder: record.nodeOrder,
                approver: record.approver,
                approvalStatus: record.approvalStatus
              } : null,
              allApprovedRecords: approvedRecords.map(r => ({
                id: r.id,
                nodeId: r.nodeId,
                nodeName: r.nodeName,
                currentNode: r.currentNode,
                currentNodeIndex: r.currentNodeIndex,
                nodeOrder: r.nodeOrder,
                approver: r.approver,
                approvalStatus: r.approvalStatus
              }))
            })
          }
          
          // 检查前面的节点是否都已经通过
          // 如果整个审批只有一个记录，通过 currentNodeIndex 来判断
          let allPreviousApproved = true
          if (overallCurrentNodeIndex !== null && approvedRecords.length > 0) {
            // 如果 currentNodeIndex >= nodeOrder，说明该节点及之前的节点都已审批
            allPreviousApproved = overallCurrentNodeIndex >= nodeOrder
          } else {
            // 如果有多个审批记录，检查每个前面的节点
            for (let i = 0; i < index; i++) {
              const prevNode = flowNodes[i]
              const prevNodeOrder = prevNode.nodeOrder !== undefined ? prevNode.nodeOrder : (i + 1)
              
              // 如果整个审批已通过，且 currentNodeIndex >= prevNodeOrder，说明前面的节点已通过
              if (isOverallApproved && overallCurrentNodeIndex !== null && overallCurrentNodeIndex >= prevNodeOrder) {
                continue
              }
              
              const prevRecord = approvedRecords.find(r => {
                if (r.nodeId && prevNode.id && String(r.nodeId) === String(prevNode.id)) {
                  return true
                }
                if (r.nodeName && prevNode.nodeName && String(r.nodeName).trim() === String(prevNode.nodeName).trim()) {
                  return true
                }
                if (r.currentNode && prevNode.nodeName && String(r.currentNode).trim() === String(prevNode.nodeName).trim()) {
                  return true
                }
                if (r.nodeOrder !== undefined && prevNode.nodeOrder !== undefined && r.nodeOrder === prevNodeOrder) {
                  return true
                }
                return false
              })
              
              if (!prevRecord) {
                allPreviousApproved = false
                break
              }
              
              const prevStatus = (prevRecord.approvalStatus || '').toUpperCase().trim()
              if (prevStatus !== 'APPROVED') {
                allPreviousApproved = false
                break
              }
            }
          }
          
          if (record) {
            // 如果找到审批记录，使用审批记录的状态
            // 规则：
            // 1. 如果前面有节点被拒绝，且当前节点在被拒绝节点之后，则设置为 CANCELLED（已取消）
            //    即使当前记录的状态是 REJECTED，如果它在被拒绝节点之后，也应该显示为 CANCELLED
            // 2. 如果整体状态是 APPROVING（审批中），且当前节点是当前待审批的节点，应该显示为"审批中"
            // 3. 否则，使用审批记录的实际状态（包括 REJECTED）
            const recordStatus = String(record.approvalStatus || '').toUpperCase().trim()
            let approvalStatus = record.approvalStatus ? String(record.approvalStatus).toUpperCase().trim() : 'PENDING'
            
            // 判断当前节点是否是当前待审批的节点
            const isCurrentNode = overallCurrentNodeIndex !== null && 
                                  (node.nodeOrder !== undefined ? node.nodeOrder : (index + 1)) === (overallCurrentNodeIndex + 1)
            const isOverallApproving = overallStatus === 'APPROVING'
            
            // 如果前面有节点被拒绝，且当前节点在被拒绝节点之后，则设置为 CANCELLED（已取消）
            // 即使当前记录的状态是 REJECTED，如果它在被拒绝节点之后，也应该显示为 CANCELLED
            if (hasRejected) {
              if (rejectedNodeIndex !== -1) {
                // 找到了被拒绝节点在流程中的位置
                if (index > rejectedNodeIndex) {
                  // 当前节点在被拒绝节点之后，设置为 CANCELLED
                  approvalStatus = 'CANCELLED'
                  console.log(`节点 ${node.nodeName || index + 1} (index: ${index}) 在被拒绝节点之后 (rejectedNodeIndex: ${rejectedNodeIndex})，强制设置为已取消，原始状态: ${recordStatus}`)
                } else if (index === rejectedNodeIndex) {
                  // 当前节点就是被拒绝的节点，保持 REJECTED
                  console.log(`节点 ${node.nodeName || index + 1} (index: ${index}) 是被拒绝的节点 (rejectedNodeIndex: ${rejectedNodeIndex})，保持已驳回`)
                }
              } else {
                // 如果找不到被拒绝节点在流程中的位置，使用节点顺序作为备用方案
                const rejectedRecord = approvedRecords[rejectedRecordIndex]
                const currentNodeOrder = node.nodeOrder !== undefined ? node.nodeOrder : (index + 1)
                const rejectedNodeOrder = rejectedRecord.nodeOrder !== undefined ? rejectedRecord.nodeOrder : 1
                
                if (currentNodeOrder > rejectedNodeOrder) {
                  // 当前节点在被驳回节点之后，设置为 CANCELLED
                  approvalStatus = 'CANCELLED'
                  console.log(`节点 ${node.nodeName || index + 1} (nodeOrder: ${currentNodeOrder}) 在被驳回节点之后 (rejectedNodeOrder: ${rejectedNodeOrder})，强制设置为已取消，原始状态: ${recordStatus}`)
                } else if (currentNodeOrder === rejectedNodeOrder && recordStatus === 'REJECTED') {
                  // 当前节点就是被驳回的节点，保持 REJECTED
                  console.log(`节点 ${node.nodeName || index + 1} (nodeOrder: ${currentNodeOrder}) 是被驳回的节点 (rejectedNodeOrder: ${rejectedNodeOrder})，保持已驳回`)
                }
              }
            } else if (isOverallApproving && isCurrentNode && (recordStatus === 'PENDING' || recordStatus === 'APPROVING')) {
              // 如果整体状态是 APPROVING（审批中），且当前节点是当前待审批的节点，应该显示为"审批中"
              approvalStatus = 'APPROVING'
              console.log(`节点 ${node.nodeName || index + 1} 整体状态是审批中，且是当前节点，设置为审批中，原始状态: ${recordStatus}`)
            }
            
            return {
              ...record,
              nodeId: node.id,
              nodeOrder: node.nodeOrder !== undefined ? node.nodeOrder : (index + 1),
              currentNode: record.nodeName || record.currentNode || node.nodeName || `节点${index + 1}`,
              statusName: getStatusName(approvalStatus),
              statusColor: getStatusColor(approvalStatus),
              createTimeFormatted: record.approvalTime
                ? formatDateTime(record.approvalTime)
                : record.createTime
                ? formatDateTime(record.createTime)
                : '',
              approverName: record.approverName || record.approver || '',
              approvalStatus: approvalStatus
            }
          } else {
            // 调试信息：未找到审批记录
            console.log(`节点 ${node.nodeName || index + 1} 未找到审批记录`)
            
            // 如果没有找到审批记录，判断节点状态：
            // 规则：
            // 1. 如果前面有节点被拒绝，且当前节点在被拒绝节点之后，则设置为 CANCELLED（已取消）
            // 2. 如果整个审批已驳回，且当前节点不在被拒绝节点之后，当前节点应该显示为"已驳回"
            // 3. 如果前面的节点还没有全部通过，当前节点应该等待
            // 4. 如果整体状态是 APPROVING（审批中），且当前节点是当前待审批的节点，应该显示为"审批中"
            // 5. 否则，显示为"待审批"
            // 注意：不要因为整个审批已通过就把所有节点都显示为"已通过"，只有实际已通过的节点才显示为"已通过"
            
            // 判断当前节点是否是当前待审批的节点
            // currentNodeIndex 从0开始，nodeOrder 从1开始，所以需要 +1 来匹配
            const nodeOrder = node.nodeOrder !== undefined ? node.nodeOrder : (index + 1)
            const isCurrentNode = overallCurrentNodeIndex !== null && 
                                  nodeOrder === (overallCurrentNodeIndex + 1)
            const isOverallApproving = overallStatus === 'APPROVING'
            
            let nodeStatus = null
            if (!nodeStatus) {
              if (hasRejected && rejectedNodeIndex !== -1 && index > rejectedNodeIndex) {
                // 如果前面有节点被拒绝，且当前节点在被拒绝节点之后，则设置为 CANCELLED（已取消）
                nodeStatus = 'CANCELLED'
                console.log(`节点 ${node.nodeName || index + 1} 前面节点被拒绝，设置为已取消`)
              } else if (isOverallRejected && (rejectedNodeIndex === -1 || index <= rejectedNodeIndex)) {
                // 如果整个审批已驳回，且当前节点不在被拒绝节点之后，当前节点应该显示为"已驳回"
                nodeStatus = 'REJECTED'
                console.log(`节点 ${node.nodeName || index + 1} 整个审批已驳回，设置为已驳回`)
              } else if (!allPreviousApproved) {
                // 前面的节点还没有全部通过，当前节点应该等待
                nodeStatus = 'WAITING'
                console.log(`节点 ${node.nodeName || index + 1} 前面节点未全部通过，设置为等待中`)
              } else if (isOverallApproving && isCurrentNode) {
                // 如果整体状态是 APPROVING（审批中），且当前节点是当前待审批的节点，应该显示为"审批中"
                nodeStatus = 'APPROVING'
                console.log(`节点 ${node.nodeName || index + 1} 整体状态是审批中，且是当前节点，设置为审批中`)
              } else {
                // 前面的节点都已经通过，但当前节点还没有审批记录，应该显示为"待审批"
                nodeStatus = 'PENDING'
                console.log(`节点 ${node.nodeName || index + 1} 没有审批记录，设置为待审批`)
              }
            }
            
            // 获取节点审批人信息 - 直接使用节点配置中的审批人姓名
            const approverName = node.approverName || '待指定'
            
            return {
              nodeId: node.id,
              nodeOrder: node.nodeOrder !== undefined ? node.nodeOrder : (index + 1),
              currentNode: node.nodeName || `节点${index + 1}`,
              approvalStatus: nodeStatus,
              statusName: getStatusName(nodeStatus),
              statusColor: getStatusColor(nodeStatus),
              createTimeFormatted: '',
              approverName: approverName,
              rejectReason: null
            }
          }
        })
        
        // 按节点顺序排序
        approvalRecords.value = mergedRecords.sort((a, b) => {
          const orderA = a.nodeOrder !== undefined ? a.nodeOrder : 999
          const orderB = b.nodeOrder !== undefined ? b.nodeOrder : 999
          return orderA - orderB
        })

        // 从当前审批节点获取审批人信息并填充到审批详情中
        if (!approvalDetail.value.approver || !approvalDetail.value.approver.trim()) {
          // 方法1: 从审批记录中找到当前正在审批的节点
          const currentRecord = approvalRecords.value.find(r => {
            const status = (r.approvalStatus || '').toUpperCase()
            return status === 'APPROVING' || status === 'PENDING'
          })
          
          if (currentRecord && currentRecord.approverName && currentRecord.approverName !== '待审批' && currentRecord.approverName !== '待指定') {
            approvalDetail.value.approver = currentRecord.approverName
          } else {
            // 方法2: 直接从节点配置中获取当前节点的审批人
            // 计算已审批完成的节点数量，当前节点就是下一个
            const currentApprovedCount = approvedRecords.filter(r => r.approvalStatus === 'APPROVED').length
            if (currentApprovedCount < flowNodes.length) {
              const currentNode = flowNodes[currentApprovedCount]
              if (currentNode && currentNode.approverName) {
                approvalDetail.value.approver = currentNode.approverName
              }
            }
          }
        }
      } else {
        // 如果没有节点配置，只显示已审批的记录
        // 直接使用接口返回的状态，不做任何修改
        const records = approvedRecords.map((record) => {
          const normalizedStatus = record.approvalStatus ? String(record.approvalStatus).toUpperCase().trim() : 'PENDING'
          
          return {
            ...record,
            currentNode: record.nodeName || record.currentNode || '审批节点',
            statusName: getStatusName(normalizedStatus),
            statusColor: getStatusColor(normalizedStatus),
            createTimeFormatted: record.approvalTime
              ? formatDateTime(record.approvalTime)
              : record.createTime
              ? formatDateTime(record.createTime)
              : '',
            approverName: record.approverName || record.approver || '',
            approvalStatus: normalizedStatus
          }
        })
        
        // 按节点顺序排序（nodeOrder），确保所有节点按顺序显示
        approvalRecords.value = records.sort((a, b) => {
          const orderA = a.nodeOrder !== undefined ? a.nodeOrder : (a.nodeId || 0)
          const orderB = b.nodeOrder !== undefined ? b.nodeOrder : (b.nodeId || 0)
          return orderA - orderB
        })

        // 如果没有节点配置，也从审批记录中获取当前审批人
        if (!approvalDetail.value.approver || !approvalDetail.value.approver.trim()) {
          const currentRecord = approvalRecords.value.find(r => {
            const status = (r.approvalStatus || '').toUpperCase()
            return status === 'APPROVING' || status === 'PENDING'
          })
          
          if (currentRecord) {
            approvalDetail.value.approver = currentRecord.approverName || currentRecord.approver || '-'
          }
        }
      }

      // 业务详情由 BusinessDetail 组件自动加载
    }
  } catch (error) {
    console.error('加载审批详情失败:', error)
    ElMessage.error(error.message || '加载失败')
  } finally {
    loading.value = false
  }
}

// 检查前一个节点是否已通过
const checkPreviousNodeApproved = () => {
  if (!approvalDetail.value || !approvalRecords.value || approvalRecords.value.length === 0) {
    return { canOperate: true, message: '' }
  }
  
  // 获取当前用户信息
  let userInfo = {}
  try {
    const userInfoStr = localStorage.getItem('userInfo')
    if (userInfoStr) {
      userInfo = JSON.parse(userInfoStr)
    }
  } catch (e) {
    console.warn('解析用户信息失败:', e)
    userInfo = {}
  }
  const currentUser = userInfo.username || userInfo.realName || ''
  
  // 查找当前用户待审批的记录（状态为 PENDING 或 APPROVING）
  // 注意：同一个审批人可能出现在多个非连续节点中（如节点1和节点3都是张三）
  // 需要优先通过 currentNodeIndex 来查找当前应该轮到的节点，而不是通过审批人匹配
  let currentRecord = null
  
  // 首先尝试从审批详情中获取当前节点索引
  const approvalCurrentNodeIndex = approvalDetail.value?.currentNodeIndex !== undefined ? 
                                   approvalDetail.value.currentNodeIndex : null
  
  if (approvalCurrentNodeIndex !== null && approvalCurrentNodeIndex !== undefined) {
    // 优先通过 currentNodeIndex 查找当前记录
    currentRecord = approvalRecords.value.find(r => {
      const recordNodeIndex = r.currentNodeIndex !== undefined ? r.currentNodeIndex : 
                             (r.nodeOrder !== undefined ? r.nodeOrder - 1 : null)
      return recordNodeIndex === approvalCurrentNodeIndex
    })
  }
  
  // 如果通过 currentNodeIndex 找不到，再通过审批人匹配查找
  if (!currentRecord) {
    currentRecord = approvalRecords.value.find(r => {
      const status = (r.approvalStatus || '').toUpperCase().trim()
      const isPendingOrApproving = status === 'PENDING' || status === 'APPROVING'
      
      // 检查是否是当前用户的审批记录
      const approverMatch = (r.approver && currentUser && r.approver.includes(currentUser)) ||
                            (r.approverName && currentUser && r.approverName.includes(currentUser))
      
      return isPendingOrApproving && approverMatch
    })
  }
  
  if (!currentRecord) {
    // 如果找不到当前节点记录，允许操作（可能是第一个节点或已处理完）
    return { canOperate: true, message: '' }
  }
  
  // 获取当前节点的索引（优先使用 currentNodeIndex，其次使用 nodeOrder）
  const currentNodeIndex = currentRecord.currentNodeIndex !== undefined ? currentRecord.currentNodeIndex : 
                           (currentRecord.nodeOrder !== undefined ? currentRecord.nodeOrder : null)
  
  if (currentNodeIndex === null || currentNodeIndex === undefined) {
    // 如果无法确定节点索引，允许操作（兼容处理）
    return { canOperate: true, message: '' }
  }
  
  // 如果是第一个节点（索引为1），直接允许操作
  if (currentNodeIndex <= 1) {
    return { canOperate: true, message: '' }
  }
  
  // 查找前一个节点的审批记录（节点索引为 currentNodeIndex - 1）
  const previousNodeIndex = currentNodeIndex - 1
  const previousRecord = approvalRecords.value.find(r => {
    const recordNodeIndex = r.currentNodeIndex !== undefined ? r.currentNodeIndex : 
                           (r.nodeOrder !== undefined ? r.nodeOrder : null)
    return recordNodeIndex === previousNodeIndex
  })
  
  if (!previousRecord) {
    // 如果找不到前一个节点记录，不允许操作
    return { 
      canOperate: false, 
      message: `请等待上一个节点审批完毕` 
    }
  }
  
  // 检查前一个节点的审批状态
  const previousStatus = (previousRecord.approvalStatus || '').toUpperCase().trim()
  
  if (previousStatus !== 'APPROVED') {
    // 前一个节点未通过，不允许操作
    const previousApproverName = previousRecord.approverName || previousRecord.approver || '未知审批人'
    return { 
      canOperate: false, 
      message: `请等待上一个节点：${previousApproverName}审批完毕` 
    }
  }
  
  // 前一个节点已通过，允许操作
  return { canOperate: true, message: '' }
}

// 处理通过
const handleApprove = async () => {
  // 检查前一个节点是否已通过
  const checkResult = checkPreviousNodeApproved()
  if (!checkResult.canOperate) {
    ElMessage.warning(checkResult.message)
    return
  }
  
  try {
    await ElMessageBox.confirm('确定要通过该审批吗？', '确认通过', {
      type: 'warning'
    })
    await submitApproval('APPROVED')
  } catch (error) {
    if (error !== 'cancel') {
      console.error('审批操作失败:', error)
    }
  }
}

// 处理拒绝
const handleReject = () => {
  // 检查前一个节点是否已通过
  const checkResult = checkPreviousNodeApproved()
  if (!checkResult.canOperate) {
    ElMessage.warning(checkResult.message)
    return
  }
  
  rejectDialogVisible.value = true
}

// 提交拒绝
const handleSubmitReject = async () => {
  // 再次检查前一个节点是否已通过（防止在对话框打开期间状态变化）
  const checkResult = checkPreviousNodeApproved()
  if (!checkResult.canOperate) {
    ElMessage.warning(checkResult.message)
    rejectDialogVisible.value = false
    return
  }
  
  if (!rejectReason.value || !rejectReason.value.trim()) {
    ElMessage.warning('驳回意见不能为空')
    return
  }
  await submitApproval('REJECTED', rejectReason.value.trim())
}

// 提交审批
const submitApproval = async (status, rejectReasonValue = null) => {
  processing.value = true
  try {
    let userInfo = {}
    try {
      const userInfoStr = localStorage.getItem('userInfo')
      if (userInfoStr) {
        userInfo = JSON.parse(userInfoStr)
      }
    } catch (e) {
      console.warn('解析用户信息失败:', e)
      userInfo = {}
    }
    const approver = userInfo.username || userInfo.realName || ''

    const requestData = {
      approvalId: route.params.id,
      approvalStatus: status,
      approver: approver
    }
    if (rejectReasonValue) {
      requestData.rejectReason = rejectReasonValue
    }

    const res = await updateApprovalStatus(requestData)
    if (res.code === '000000') {
      ElMessage.success(status === 'APPROVED' ? '审批通过' : '审批驳回')
      rejectDialogVisible.value = false
      rejectReason.value = ''
      
      // 标记已进行审批操作
      hasPerformedAction.value = true
      
      // 审批成功后，先关闭标签页，然后跳转到列表页
      // 传递审批操作前的状态，用于减少对应状态的角标数量
      const currentPath = route.path
      window.dispatchEvent(new CustomEvent('close-approval-detail-tab', { 
        detail: { path: currentPath } 
      }))
      router.push({
        path: getApprovalListPath(),
        query: {
          refresh: 'true',
          autoStatus: status === 'APPROVED' ? 'APPROVED' : 'REJECTED',
          previousStatus: previousApprovalStatus.value || '' // 传递审批操作前的状态
        }
      })
    } else {
      throw new Error(res.msg || '操作失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('提交审批失败:', error)
      ElMessage.error(error.message || '操作失败')
    }
  } finally {
    processing.value = false
  }
}


// 返回
const handleBack = () => {
  const currentPath = route.path
  
  // 如果最近有审批操作，跳转到列表页并传递状态参数
  if (lastApprovalAction.value) {
    const status = lastApprovalAction.value
    // 先关闭标签页
    window.dispatchEvent(new CustomEvent('close-approval-detail-tab', { 
      detail: { path: currentPath } 
    }))
    // 根据审批状态跳转到列表页，并传递状态参数
    const listPath = getApprovalListPath()
    router.push({
      path: listPath,
      query: {
        refresh: 'true',
        autoStatus: status === 'APPROVED' ? 'APPROVED' : 'REJECTED'
      }
    })
    // 通知 Home.vue 激活对应菜单
    try {
      window.dispatchEvent(new CustomEvent('menu-set-active', {
        detail: { path: listPath }
      }))
    } catch (e) {
      console.warn('发送菜单激活事件失败:', e)
    }
    // 清除状态，避免重复触发
    lastApprovalAction.value = null
  } else if (hasPerformedAction.value) {
    // 如果进行了审批操作，已经在 submitApproval 中处理了跳转，这里不需要处理
    // 但为了安全，还是发送关闭标签页的事件并跳转到列表页
    window.dispatchEvent(new CustomEvent('close-approval-detail-tab', { 
      detail: { path: currentPath } 
    }))
    // 兜底：直接调用注入的 closeTab，确保标签被关闭
    if (typeof closeTab === 'function') {
      closeTab(currentPath)
    }
    const listPath = getApprovalListPath()
    router.push({
      path: listPath,
      query: {
        refresh: 'true'
      }
    })
    try {
      window.dispatchEvent(new CustomEvent('menu-set-active', {
        detail: { path: listPath }
      }))
    } catch (e) {
      console.warn('发送菜单激活事件失败:', e)
    }
  } else {
    // 检查当前详情页的状态（可能是从 PENDING 变为 APPROVING）
    const currentStatus = approvalDetail.value?.approvalStatus 
      ? (approvalDetail.value.approvalStatus || '').toUpperCase() 
      : null
    
    // 获取进入详情前的状态
    const previousStatus = previousApprovalStatus.value ? previousApprovalStatus.value.toUpperCase() : null
    
    // 如果当前状态是审批中，且初始状态不是审批中（说明是从待审批变为审批中）
    // 或者当前状态是审批中，且没有进行审批操作
    // 返回时自动切换到审批中状态并刷新列表，同时刷新角标
    if (currentStatus === 'APPROVING' && !hasPerformedAction.value) {
      console.log('从审批中详情返回（未操作），自动切换到审批中状态并刷新列表')
      // 先关闭标签页
      window.dispatchEvent(new CustomEvent('close-approval-detail-tab', { 
        detail: { path: currentPath } 
      }))
      if (typeof closeTab === 'function') {
        closeTab(currentPath)
      }
      const listPath = getApprovalListPath()
      router.push({
        path: listPath,
        query: {
          refresh: 'true',
          autoStatus: 'APPROVING',
          // 传递 previousStatus，用于刷新角标
          previousStatus: previousStatus || 'PENDING'
        }
      })
      try {
        window.dispatchEvent(new CustomEvent('menu-set-active', {
          detail: { path: listPath }
        }))
      } catch (e) {
        console.warn('发送菜单激活事件失败:', e)
      }
    } else if (previousStatus === 'PENDING' && !hasPerformedAction.value) {
      // 如果之前是待审批状态，且没有进行审批操作，返回时刷新待审批角标
      // 这种情况可能是：打开详情后状态变为审批中，或者只是查看详情
      console.log('从待审批详情返回（未操作），刷新待审批角标')
      // 先关闭标签页
      window.dispatchEvent(new CustomEvent('close-approval-detail-tab', { 
        detail: { path: currentPath } 
      }))
      if (typeof closeTab === 'function') {
        closeTab(currentPath)
      }
      const listPath = getApprovalListPath()
      router.push({
        path: listPath,
        query: {
          refresh: 'true',
          // 传递 previousStatus，用于刷新角标
          previousStatus: 'PENDING'
        }
      })
      try {
        window.dispatchEvent(new CustomEvent('menu-set-active', {
          detail: { path: listPath }
        }))
      } catch (e) {
        console.warn('发送菜单激活事件失败:', e)
      }
    } else {
      // 统一跳转到审批列表页，而不是使用 router.back()，避免出现"未知页面"
      // 先关闭标签页
      window.dispatchEvent(new CustomEvent('close-approval-detail-tab', { 
        detail: { path: currentPath } 
      }))
      if (typeof closeTab === 'function') {
        closeTab(currentPath)
      }
      // 跳转到审批列表页
      const listPath = getApprovalListPath()
      router.push({
        path: listPath,
        query: {
          refresh: 'true'
        }
      })
      try {
        window.dispatchEvent(new CustomEvent('menu-set-active', {
          detail: { path: listPath }
        }))
      } catch (e) {
        console.warn('发送菜单激活事件失败:', e)
      }
    }
  }
}

// 格式化日期时间
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return dateStr
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}`
}

// 获取状态名称
const getStatusName = (status) => {
  if (!status) return '待审批'
  const statusUpper = String(status).toUpperCase().trim()
  const names = {
    PENDING: '待审批',
    APPROVING: '审批中',
    APPROVED: '已通过',
    REJECTED: '已驳回',
    WAITING: '等待中',
    CANCELLED: '已取消',
    CANCELED: '已取消' // 兼容美式拼写
  }
  const result = names[statusUpper] || status
  // 调试信息
  if (statusUpper === 'REJECTED') {
    console.log('getStatusName 处理 REJECTED:', { status, statusUpper, result })
  }
  return result
}

// 获取状态颜色
const getStatusColor = (status) => {
  if (!status) return '#8a8fa6'
  const statusUpper = String(status).toUpperCase().trim()
  const colors = {
    PENDING: '#f59f00',
    APPROVING: '#1890ff',
    APPROVED: '#52c41a',
    REJECTED: '#ff4d4f',
    WAITING: '#8a8fa6',
    CANCELLED: '#8a8fa6',
    CANCELED: '#8a8fa6' // 兼容美式拼写
  }
  return colors[statusUpper] || '#8a8fa6'
}

// 获取状态标签类型
const getStatusTagType = (status) => {
  const types = {
    PENDING: 'warning',
    APPROVING: 'primary',
    WAITING: 'info',
    APPROVED: 'success',
    REJECTED: 'danger'
  }
  return types[status] || 'info'
}

// 获取业务类型名称
const getBusinessTypeName = (type) => {
  const names = {
    CONTRACT: '合同',
    RETURN_ORDER: '返货单',
    TRAVEL_EXPENSE: '报销申请',
    BUSINESS_TRIP: '出差申请',
    CUSTOMER_DEAL_RECORD: '成交台账'
  }
  return names[type] || type
}

// 获取业务类型标签类型
const getBusinessTypeTagType = () => {
  return 'info'
}

// 初始化
onMounted(() => {
  loadApprovalDetail()
})
</script>

<style scoped>
.approval-detail {
  padding: 20px;

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
    }
  }

  .detail-content {
    .info-card {
      margin-bottom: 20px;

      .section-title {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
      }

      .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .business-summary {
        padding: 12px;
        background: #f5f7fa;
        border-radius: 4px;

        p {
          margin: 0;
          color: #606266;
        }
      }

      .reject-reason {
        color: #f56c6c;
      }
    }

    .action-bar {
      margin-top: 20px;
      padding: 20px;
      background: #f5f7fa;
      border-radius: 4px;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 16px;
    }

    .action-btn {
      min-width: 120px;
      padding: 12px 24px;
      font-size: 16px;
    }
  }
}
</style>

